import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { useComponentTranslation } from "@/services/i18n";
import { FiGlobe, FiLoader, FiEdit3, FiUpload } from "react-icons/fi";
import Button from "@/components/loading-button";
import useStore from "@/services/store";
import api from "@/services/api";
import { readFileAsync } from "@/utils";

const translations = {
  en: {
    title: "Company Profile",
    scrape_subtitle: "Paste your website to auto-fill your company profile.",
    website_placeholder: "https://www.yourcompany.com",
    analyze: "Import",
    analyzing: "Importing...",
    analyze_success: "Company info imported! Review and save below.",
    analyze_error: "Failed to analyze website",
    enter_manually: "Enter manually instead",
    back_to_scrape: "Import from website",
    company_name: "Company name",
    company_name_placeholder: "Enter company name",
    description: "Description",
    description_placeholder: "A short pitch about your company...",
    logo: "Logo",
    logo_extracted: "Extracted from your website",
    remove_logo: "Remove",
    website: "Website",
    website_form_placeholder: "https://example.com",
    industry: "Industry",
    industry_placeholder: "e.g. Technology, Healthcare",
    location: "Location",
    location_placeholder: "e.g. Paris, France",
    save: "Save Changes",
    success: "Company information updated",
  },
  fr: {
    title: "Profil Entreprise",
    scrape_subtitle: "Collez votre site web pour remplir automatiquement votre profil entreprise.",
    website_placeholder: "https://www.votreentreprise.com",
    analyze: "Importer",
    analyzing: "Import...",
    analyze_success: "Infos importées ! Vérifiez et enregistrez ci-dessous.",
    analyze_error: "Échec de l'analyse du site",
    enter_manually: "Saisir manuellement",
    back_to_scrape: "Importer depuis un site web",
    company_name: "Nom de l'entreprise",
    company_name_placeholder: "Entrez le nom de l'entreprise",
    description: "Description",
    description_placeholder: "Un court pitch sur votre entreprise...",
    logo: "Logo",
    logo_extracted: "Extrait de votre site web",
    remove_logo: "Supprimer",
    website: "Site web",
    website_form_placeholder: "https://exemple.com",
    industry: "Secteur",
    industry_placeholder: "ex. Technologie, Santé",
    location: "Localisation",
    location_placeholder: "ex. Paris, France",
    save: "Enregistrer",
    success: "Informations de l'entreprise mises à jour",
  },
};

export default function Company() {
  const { t } = useComponentTranslation(translations);
  const { organization, setOrganization } = useStore();

  const alreadyHasProfile = !!(organization?.name && organization?.description);
  const [mode, setMode] = useState(alreadyHasProfile ? "manual" : "scrape");
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);

  const update = (fields) => setOrganization({ ...organization, ...fields });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { name, description, logo, website, industry, location } = organization;
      const { ok, data } = await api.put("/organization", { organization_id: organization._id, name, description, logo, website, industry, location });
      if (!ok) throw new Error("Error updating company");
      setOrganization(data);
      toast.success(t("success"));
    } catch (e) {
      console.error(e);
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!organization?.website) return;
    setGenerating(true);
    try {
      const { ok, data } = await api.post("/organization/generate-from-website", { organization_id: organization._id, url: organization.website });
      if (!ok) throw new Error(t("analyze_error"));
      setOrganization(data);
      toast.success(t("analyze_success"));
      setMode("manual");
    } catch (e) {
      console.error(e);
      toast.error(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const rawBody = await readFileAsync(file);
      const response = await api.post("/file", { file: { rawBody, name: file.name }, folder: "/logo" });
      if (!response.ok) throw response;
      update({ logo: response.data });
    } catch (error) {
      console.error("Logo upload error:", error);
      toast.error("Failed to upload logo");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-8">{t("title")}</h1>

      {mode === "scrape" ? (
        <div className="space-y-6">
          <p className="text-sm text-gray-500">{t("scrape_subtitle")}</p>

          <div>
            <input
              type="url"
              value={organization?.website || ""}
              onChange={(e) => update({ website: e.target.value })}
              className="input"
              placeholder={t("website_placeholder")}
            />
          </div>

          <Button loading={generating} onClick={handleAnalyze} disabled={!organization?.website} className="btn btn-primary w-full">
            {generating ? (
              <span className="flex items-center gap-2">
                <FiLoader className="w-4 h-4 animate-spin" />
                {t("analyzing")}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <FiGlobe className="w-4 h-4" />
                {t("analyze")}
              </span>
            )}
          </Button>

          <div className="flex justify-end">
            <button type="button" onClick={() => setMode("manual")} className="text-sm text-gray-500 hover:text-primary transition-colors flex items-center gap-1.5">
              <FiEdit3 className="w-3.5 h-3.5" />
              {t("enter_manually")}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Logo + Name row */}
          <div className="flex items-start gap-4">
            <div className="shrink-0">
              {organization?.logo ? (
                <div>
                  <div className="w-16 h-16 rounded-lg border border-gray-200 bg-gray-50 overflow-hidden">
                    <img src={organization.logo} alt="Logo" className="w-full h-full object-contain" />
                  </div>
                  <button type="button" onClick={() => update({ logo: "" })} className="text-xs text-red-500 hover:text-red-600 mt-1">
                    {t("remove_logo")}
                  </button>
                </div>
              ) : (
                <label className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center cursor-pointer hover:border-primary hover:bg-gray-100 transition-colors">
                  {uploading ? <FiLoader className="w-5 h-5 text-gray-400 animate-spin" /> : <FiUpload className="w-5 h-5 text-gray-400" />}
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" disabled={uploading} />
                </label>
              )}
            </div>
            <div className="flex-1">
              <label className="input-label">{t("company_name")}</label>
              <input
                type="text"
                value={organization?.name || ""}
                onChange={(e) => update({ name: e.target.value })}
                className="input"
                placeholder={t("company_name_placeholder")}
              />
            </div>
          </div>

          <div>
            <label className="input-label">{t("description")}</label>
            <textarea
              value={organization?.description || ""}
              onChange={(e) => {
                update({ description: e.target.value });
                e.target.style.height = "auto";
                e.target.style.height = e.target.scrollHeight + "px";
              }}
              ref={(el) => {
                if (el) {
                  el.style.height = "auto";
                  el.style.height = el.scrollHeight + "px";
                }
              }}
              className="input resize-none overflow-hidden"
              placeholder={t("description_placeholder")}
              rows={2}
            />
          </div>

          <div>
            <label className="input-label">{t("website")}</label>
            <input
              type="url"
              value={organization?.website || ""}
              onChange={(e) => update({ website: e.target.value })}
              className="input"
              placeholder={t("website_form_placeholder")}
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="input-label">{t("industry")}</label>
              <input
                type="text"
                value={organization?.industry || ""}
                onChange={(e) => update({ industry: e.target.value })}
                className="input"
                placeholder={t("industry_placeholder")}
              />
            </div>
            <div>
              <label className="input-label">{t("location")}</label>
              <input
                type="text"
                value={organization?.location || ""}
                onChange={(e) => update({ location: e.target.value })}
                className="input"
                placeholder={t("location_placeholder")}
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-4">
            <button type="button" onClick={() => setMode("scrape")} className="text-sm text-gray-500 hover:text-primary transition-colors flex items-center gap-1.5">
              <FiGlobe className="w-3.5 h-3.5" />
              {t("back_to_scrape")}
            </button>
            <Button loading={loading} type="submit" className="btn btn-primary">
              {t("save")}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
